import { useMutation, type UseMutationResult } from '@tanstack/react-query';

import { useAuth } from '@/shared/auth';

import {
  uploadRainFile,
  type IUploadRainFileResult,
  type RainUploadError,
} from '../utils/upload';

export interface IUseUploadRainFileVars {
  organization: string;
  workspace: string;
  agentId: string;
  threadId: string;
  /**
   * Optional path segment scoping the upload to an entity the thread belongs to
   * (e.g. `automations/{id}` or `automations/{id}/runs/{runId}`). Omit to scope
   * the upload directly to the workspace agent.
   */
  scope?: string;
  file: File;
  signal?: AbortSignal;
  onProgress?: (loaded: number, total: number) => void;
}

export function useUploadRainFile(): UseMutationResult<
  IUploadRainFileResult,
  RainUploadError,
  IUseUploadRainFileVars
> {
  const { getAccessTokenSilently } = useAuth();

  return useMutation<
    IUploadRainFileResult,
    RainUploadError,
    IUseUploadRainFileVars
  >({
    mutationFn: async ({
      organization,
      workspace,
      agentId,
      threadId,
      scope,
      file,
      signal,
      onProgress,
    }) => {
      const accessToken = await getAccessTokenSilently();
      return uploadRainFile({
        organization,
        workspace,
        agentId,
        threadId,
        scope,
        file,
        accessToken,
        signal,
        onProgress,
      });
    },
  });
}
